import React, { useEffect, useMemo } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import dayjs from 'dayjs';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { formatFloat } from '../../../helpers';
import { DateGroupBy, useDashboardProductionChartLazyQuery } from '../../../services';

// ── Constants ─────────────────────────────────────────────────────────────────

// Index 0 = Corte y empaque (green), index 1 = Extrusión (blue), 2 = Peletizado, 3 = Lavado
const COLORS = ['#388e3c', '#1976d2', '#f57c00', '#7b1fa2', '#d32f2f', '#0288d1'];

// ── Helpers ───────────────────────────────────────────────────────────────────

function renderTooltip(bg: string, border: string, textColor: string) {
    return function TooltipContent(props: any) {
        const { active, payload, label } = props;
        if (!active || !payload?.length) return null;
        return (
            <div style={{
                backgroundColor: bg,
                border: `1px solid ${border}`,
                borderRadius: 4,
                padding: '8px 12px',
                fontSize: 12,
                color: textColor,
            }}>
                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{label}</div>
                {payload.map((entry: any, i: number) => (
                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ color: entry.color }}>■</span>
                        <span>{entry.name}:</span>
                        <span style={{ fontWeight: 'bold' }}>{formatFloat(entry.value)} kg</span>
                    </div>
                ))}
            </div>
        );
    };
}

function renderLegend(textColor: string) {
    return function LegendContent(props: any) {
        const { payload } = props;
        if (!payload) return null;
        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px 16px', fontSize: 12 }}>
                {payload.map((entry: any, index: number) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <svg width={10} height={10}>
                            <rect width={10} height={10} fill={entry.color} />
                        </svg>
                        <span style={{ color: textColor, fontSize: 12 }}>{entry.value}</span>
                    </div>
                ))}
            </div>
        );
    };
}

function hexToRgba(hex: string, opacity: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

const DAY_LABELS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

// ── Helpers ───────────────────────────────────────────────────────────────────

interface ProductionRecord {
    order_production_type_name?: string | null;
    kilos: number;
    year: number;
    month: number;
    day?: number | null;
}

// A week can straddle two months (even two years), so every day carries its
// own full (year, month, day) identity — month 1-indexed to match the SQL
// output — instead of assuming one month for the whole week.
interface WeekDay {
    year: number;
    month: number;
    day: number;
    dayOfWeek: number;
    label: string;
}

function aggregateByDay(
    records: ProductionRecord[],
    days: WeekDay[],
    productionTypes: string[],
    suffix: string,
): Record<string, number>[] {
    return days.map(({ year, month, day }) => {
        const row: Record<string, number> = {};

        productionTypes.forEach((type) => {
            row[`${type}_${suffix}`] = 0;
        });

        records
            .filter((r) => r.day === day && r.month === month && r.year === year)
            .forEach((r) => {
                if (r.order_production_type_name) {
                    row[`${r.order_production_type_name}_${suffix}`] += r.kilos;
                }
            });

        return row;
    });
}

// ── Component ─────────────────────────────────────────────────────────────────

export function DashboardProductionChart() {
    const theme = useTheme();
    const isLg = useMediaQuery(theme.breakpoints.up('lg'));
    const prevWeekLabel = isLg ? 'semana anterior' : 'anterior';
    // Stable per mount so the memos below don't recompute every render
    const today = useMemo(() => dayjs(), []);

    // The two weeks shown span 14 consecutive days (prev week Monday -> this
    // week Sunday), which can touch at most two calendar months. The backend
    // summary is fetched per calendar month, so two windows cover everything:
    // A = month of the earliest visible day, B = month of the latest. When the
    // 14 days fit in one month, A === B and B's records are dropped (dedupe).
    const weekStart = useMemo(() => today.startOf('week'), [today]);
    const prevWeekStart = useMemo(() => weekStart.subtract(7, 'day'), [weekStart]);
    const weekEnd = useMemo(() => weekStart.add(6, 'day'), [weekStart]);

    const yearA = prevWeekStart.year();
    const monthA = prevWeekStart.month(); // 0-indexed (server input)
    const yearB = weekEnd.year();
    const monthB = weekEnd.month(); // 0-indexed (server input)

    const [query, { data }] = useDashboardProductionChartLazyQuery();

    useEffect(() => {
        void query({
            variables: {
                yearA,
                monthA,
                yearB,
                monthB,
                dateGroupBy: DateGroupBy.Day,
            },
        });
    }, [query, yearA, monthA, yearB, monthB]);

    // All 7 days of the current week
    const weekDays = useMemo<WeekDay[]>(() => {
        return Array.from({ length: 7 }, (_, i) => {
            const d = weekStart.add(i, 'day');
            return {
                year: d.year(),
                month: d.month() + 1, // 1-indexed (matches SQL output)
                day: d.date(),
                dayOfWeek: d.day(),
                label: DAY_LABELS[d.day()],
            };
        });
    }, [weekStart]);

    // All 7 days of the previous week (same day-of-week positions)
    const prevWeekDays = useMemo<WeekDay[]>(() => {
        return Array.from({ length: 7 }, (_, i) => {
            const d = prevWeekStart.add(i, 'day');
            return {
                year: d.year(),
                month: d.month() + 1, // 1-indexed (matches SQL output)
                day: d.date(),
                dayOfWeek: d.day(),
                label: DAY_LABELS[d.day()],
            };
        });
    }, [prevWeekStart]);

    // One deduplicated record pool for both weeks: days match on their exact
    // (year, month, day), so which window a record came from doesn't matter.
    const allRecords = useMemo<ProductionRecord[]>(() => {
        const sameWindow = yearA === yearB && monthA === monthB;
        return [
            ...(data?.summaryA.production ?? []),
            ...(sameWindow ? [] : data?.summaryB.production ?? []),
        ];
    }, [data, yearA, monthA, yearB, monthB]);

    // Distinct production type names
    const productionTypes = useMemo<string[]>(() => {
        const typeSet = new Set<string>();
        allRecords.forEach((r) => {
            if (r.order_production_type_name) typeSet.add(r.order_production_type_name);
        });
        return Array.from(typeSet);
    }, [allRecords]);

    // One chart row per day of week combining current and previous week kilos
    const chartData = useMemo(() => {
        const currRows = aggregateByDay(allRecords, weekDays, productionTypes, 'curr');
        const prevRows = aggregateByDay(allRecords, prevWeekDays, productionTypes, 'prev');

        return weekDays.map(({ label }, i) => ({
            label,
            ...prevRows[i],
            ...currRows[i],
        }));
    }, [allRecords, weekDays, prevWeekDays, productionTypes]);

    return (
        <Box sx={{ height: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={chartData}
                    margin={{ top: 8, right: 16, left: 16, bottom: 0 }}
                >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: theme.palette.text.primary }} />
                    <YAxis
                        tick={{ fontSize: 12, fill: theme.palette.text.primary }}
                        tickFormatter={(v) => formatFloat(v)}
                        width={64}
                        domain={[0, 20000]}
                    />
                    <Tooltip
                        content={renderTooltip(
                            theme.palette.background.paper,
                            theme.palette.divider,
                            theme.palette.text.primary,
                        )}
                    />
                    <Legend content={renderLegend(theme.palette.text.primary)} />
                    {productionTypes.map((type, index) => {
                        const color = COLORS[index % COLORS.length];
                        return [
                            <Bar
                                key={`${type}_prev`}
                                dataKey={`${type}_prev`}
                                name={`${type} (${prevWeekLabel})`}
                                fill={hexToRgba(color, 0.3)}
                                maxBarSize={24}
                            />,
                            <Bar
                                key={`${type}_curr`}
                                dataKey={`${type}_curr`}
                                name={type}
                                fill={color}
                                maxBarSize={24}
                            />,
                        ];
                    })}
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
}
